import { useEffect, useCallback, useReducer, Dispatch, SetStateAction } from 'react';
import useIsMounted from '@alltrails/core/hooks/useIsMounted';
import { carouselReducer, CarouselActionType } from '../utils/carouselReducer';

type CarouselControls = {
  activeIndex: number;
  navigateNext: () => void;
  navigatePrev: () => void;
  setLoadingState: (loading: boolean) => void;
  isLoading: boolean;
};

const useCarouselState = (
  carouselLength: number,
  startingIndex: number,
  onNavigate?: (newActiveIndex: number) => Promise<void>,
  count?: number,
  setCount?: Dispatch<SetStateAction<number>>,
  shouldLoop = true
): CarouselControls => {
  const [state, dispatch] = useReducer(carouselReducer, {
    activeIndex: startingIndex >= 0 && startingIndex < carouselLength ? startingIndex : 0,
    carouselLength,
    shouldLoop,
    isLoading: !!onNavigate
  });
  const { activeIndex, isLoading } = state;
  const isMounted = useIsMounted();

  const setLoadingState = (loading: boolean) => {
    dispatch({ type: CarouselActionType.SetLoading, isLoading: loading });
  };

  const onNavigated = useCallback(() => {
    if (onNavigate) {
      // used for LightboxAd in monolith
      if (count && setCount) {
        setCount(count + 1);
      }
      setLoadingState(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navigateNext = useCallback(() => {
    dispatch({ type: CarouselActionType.Next });
    onNavigated();
  }, [onNavigated]);

  const navigatePrev = useCallback(() => {
    dispatch({ type: CarouselActionType.Prev });
    onNavigated();
  }, [onNavigated]);

  useEffect(() => {
    onNavigate?.(activeIndex).finally(() => {
      if (isMounted()) {
        setLoadingState(false);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex]);

  return {
    activeIndex,
    navigateNext,
    navigatePrev,
    setLoadingState,
    isLoading
  };
};

export default useCarouselState;
